import { ComponentProps } from "react";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { ImagePresets } from "@routes/Article/components/ArticleThumbnail/utils/enums";
import { cn } from "@util/helpers";

type PodcastsStoriesProps = {
  /** Individual article */
  cardProps: ComponentProps<typeof Card>;
  /** Index of the article in the list */
  index: number;
};

/**
 * Renders a single story card for the podcasts section.
 */
const PodcastsStories = ({
  cardProps,
  index,
}: PodcastsStoriesProps): React.ReactElement => {
  const isLeadArticle = index === 0;

  const variant: CardVariations = {
    image: {
      position: "left",
      width: cn("w-full", { "w-1/2": isLeadArticle }),
      filters: {
        w: ImagePresets.LARGE,
        dpr: 1,
        f: "webp",
      },
    },
    content: {
      width: cn("w-full", { "w-1/2": isLeadArticle }),
      extraClass: cn({ "pl-4": isLeadArticle }),
      updatedTimeColor: "text-red",
      updatedTimeExtraClass: "text-7xs",
    },
    kicker: {
      color: "text-gray-515",
    },
    title: {
      color: "text-gray-850",
      size: cn("text-base", { "md:text-4xl": isLeadArticle }),
      extraClass: "font-lct my-1",
    },
  };

  return (
    <div
      className={cn("story border-t border-gray-175 py-3 lg:order-1", {
        "w-full": isLeadArticle,
        "w-full lg:w-3/12": !isLeadArticle,
        "border-none": isLeadArticle,
      })}
      data-testid="homepage-podcasts-stories-individual-component"
    >
      <Card
        {...cardProps}
        variations={variant}
        className={cn("h-full border-gray-175", {
          "lg:border-r lg:pr-3": !isLeadArticle && index !== 4,
          "lg:pl-3": !isLeadArticle && index !== 1,
        })}
      />
    </div>
  );
};

export default PodcastsStories;
